import { ref } from 'vue';

import { useCartPrediction } from '~/composables/use-cart-prediction';
import * as DataDogLogs from '~/lib/datadog/datadog';

const baseUrl = window.Shopify.routes.root;
const headers = {
  'Content-Type': 'application/json',
};
const POST_METHOD = 'POST';

const cart = ref(null);
const fetchInProgress = ref(false);

export function useCart() {
  // Get prediction functions for skeleton loading states
  const { clearPrediction, setPrediction } = useCartPrediction();

  /**
   * Check if a cart item matches a variant ID
   * Handles both string and numeric ID comparisons
   * @param {Object} cartItem - Cart item to check
   * @param {string|number} variantId - Variant ID to match against
   * @returns {boolean} True if items match
   */
  const _variantsMatch = (cartItem, variantId) => {
    const parsedVariantId = parseInt(variantId);
    return (
      cartItem.variant_id === parsedVariantId ||
      cartItem.id === parsedVariantId ||
      cartItem.variant_id === variantId ||
      cartItem.id === variantId
    );
  };

  /**
   * Get the cart, returns the cart
   * https://shopify.dev/docs/api/ajax/reference/cart#get-locale-cart-js
   *
   * @param {boolean} force
   * @returns {Promise<Object>}
   */
  const getCart = async (force = false) => {
    if (cart.value && !force) return cart.value;

    fetchInProgress.value = true;
    return fetch(`${baseUrl}cart.js`)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      })
      .then((newCartData) => {
        cart.value = newCartData;
        return newCartData;
      })
      .catch((error) => {
        cart.value = null;
        throw error;
      })
      .finally(() => {
        fetchInProgress.value = false;
      });
  };

  /**
   * Add attributes to the cart, returns the updated cart
   * https://shopify.dev/docs/api/ajax/reference/cart#update-cart-attributes
   *
   * @param {Object} attributes
   * @returns {Promise<Object>}
   */
  const addAttributes = (attributes) => {
    return updateCart({
      attributes,
    });
  };

  /**
   * Add a single variant to the cart, returns the updated cart
   *
   * @param {string} variantId
   * @param {number} quantity
   * @param {Object} properties
   * @returns {Promise<Object>}
   */
  const addSingleVariant = async (variantId, quantity = 1, properties) => {
    const currentLineItemCount = cart.value?.items?.length || 0;

    // Set prediction for skeleton display
    const existingItem = cart.value?.items?.find((item) => _variantsMatch(item, variantId));
    if (existingItem) {
      setPrediction(currentLineItemCount);
    } else {
      setPrediction(currentLineItemCount + 1);
    }

    const items = [
      {
        id: variantId,
        properties,
        quantity,
      },
    ];

    try {
      return await addToCart({ items });
    } finally {
      clearPrediction();
    }
  };

  /**
   * Add multiple items to the cart, returns the updated cart
   * https://shopify.dev/docs/api/ajax/reference/cart#post-locale-cart-add-js
   *
   * @param {Array<Object>} items
   * @returns {Promise<Object>}
   */
  const addToCart = async (items) => {
    fetchInProgress.value = true;
    try {
      await fetch(`${baseUrl}cart/add.js`, {
        body: JSON.stringify(items),
        headers,
        method: POST_METHOD,
      });
      return await getCart(true);
    } catch (error) {
      DataDogLogs.error(
        '[Allbirds composable] - useCart - addToCart - Failed to add to cart',
        { items },
        error,
      );
      throw error;
    } finally {
      fetchInProgress.value = false;
    }
  };

  /**
   * Update the cart, returns the updated cart
   * https://shopify.dev/docs/api/ajax/reference/cart#post-locale-cart-update-js
   *
   * @param {Object} updates
   * @returns {Promise<Object>}
   */
  const updateCart = async (updates) => {
    fetchInProgress.value = true;
    try {
      await fetch(`${baseUrl}cart/update.js`, {
        body: JSON.stringify(updates),
        headers,
        method: POST_METHOD,
      });
      return await getCart(true);
    } catch (error) {
      DataDogLogs.error(
        '[Allbirds composable] - useCart - updateCart - Failed to update cart',
        { updates },
        error,
      );
      throw error;
    } finally {
      fetchInProgress.value = false;
    }
  };

  /**
   * Update a single line item, returns the updated cart
   *
   * @param {string} id the variant id or the line item id
   * @param {number} quantity
   * @returns {Promise<Object>}
   */
  const updateSingleLineItem = async (id, quantity = 1) => {
    // Set prediction for removals only
    if (quantity === 0) {
      const currentLineItemCount = cart.value?.items?.length || 0;
      setPrediction(currentLineItemCount - 1);
    }

    const updates = {
      [id]: quantity,
    };

    try {
      return await updateCart({ updates });
    } finally {
      if (quantity === 0) {
        clearPrediction();
      }
    }
  };

  /**
   * Remove a single line item, returns the updated cart
   *
   * @param {string} id the variant id or the line item id
   * @returns {Promise<Object>}
   */
  const removeLineItem = async (id) => {
    // Predictions are handled by updateSingleLineItem
    return updateSingleLineItem(id, 0);
  };

  /**
   * Clear the cart, returns the updated cart
   * https://shopify.dev/docs/api/ajax/reference/cart#post-locale-cart-clear-js
   *
   * @returns {Promise<Object>}
   */
  const clearCart = async () => {
    fetchInProgress.value = true;
    try {
      const response = await fetch(`${baseUrl}cart/clear.js`, {
        headers,
        method: POST_METHOD,
      });
      return await response.json();
    } catch (error) {
      DataDogLogs.error(
        '[Allbirds composable] - useCart - clearCart - Failed to clear cart',
        {},
        error,
      );
      throw error;
    } finally {
      fetchInProgress.value = false;
    }
  };

  return {
    addAttributes,
    addSingleVariant,
    addToCart,
    cart,
    clearCart,
    getCart,
    isLoading: fetchInProgress,
    removeLineItem,
    updateCart,
    updateSingleLineItem,
  };
}
